import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle, MagnifyingGlass, MapPin } from '@phosphor-icons/react';
import { mapResponseError } from '@mybusinessmate/api-client';
import { AuthHeading } from '../../../../../components/auth/auth-heading';
import { OnboardingHeader } from '../../../../../components/auth/onboarding-header';
import { Icon } from '../../../../../components/Icon';
import { Banner } from '../../../../../components/ui/banner';
import { Button } from '../../../../../components/ui/button';
import { Input } from '../../../../../components/ui/input';
import { Skeleton } from '../../../../../components/ui/skeleton';
import { textLinkClass } from '../../../../../components/ui/text-link';
import { cx } from '../../../../../lib/cx';
import { api } from '../../../../../lib/api';
import { useOnboardingStore } from '../../../../../lib/onboarding-store';

export const Route = createFileRoute('/subdomain/app/_auth/onboarding/business')({
  component: BusinessStep,
});

/** Debounces a fast-changing value so we don't hit Google Places on every keystroke. */
function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

function BusinessStep() {
  const navigate = useNavigate();
  const { draft, updateDraft } = useOnboardingStore();
  const [manual, setManual] = useState(false);

  const term = useDebounced(draft.businessName.trim(), 300);
  // Picking a result writes its name into the field; don't re-search for the name we just picked.
  const alreadyPicked = Boolean(draft.googlePlaceId);
  const search = useQuery({
    queryKey: ['places', term],
    enabled: term.length >= 2 && !alreadyPicked && !manual,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const res = await api.api.business.places.search.$get({ query: { q: term } });
      if (!res.ok) throw await mapResponseError(res);
      return (await res.json()).places;
    },
  });

  const places = search.data ?? [];
  const showNoMatch = !alreadyPicked && !manual && term.length >= 2 && search.isSuccess && places.length === 0;
  const canContinue = draft.businessName.trim().length > 0 && (alreadyPicked || draft.address.trim().length > 0);

  return (
    <>
      <OnboardingHeader step={2} backTo="/subdomain/app/onboarding/industry" />
      <AuthHeading title="Let’s find your business" subtitle="Search the name as it appears on Google Maps." />

      <Input
        label="Business name"
        leadingIcon={MagnifyingGlass}
        value={draft.businessName}
        autoComplete="organization"
        onChange={(e) =>
          updateDraft({ businessName: e.target.value, googlePlaceId: undefined, address: manual ? draft.address : '' })
        }
      />

      {search.isFetching && !alreadyPicked ? (
        <div className="flex flex-col gap-(--mbm-space-3)" aria-label="Searching Google Maps" aria-busy>
          <Skeleton className="h-(--mbm-size-list-row-default) rounded-(--mbm-radius-md)" />
          <Skeleton className="h-(--mbm-size-list-row-default) rounded-(--mbm-radius-md)" />
        </div>
      ) : null}

      {search.isError ? (
        <Banner tone="warning">
          Couldn’t search Google Maps right now. You can add your address manually instead.
        </Banner>
      ) : null}

      {places.length > 0 && !alreadyPicked && !manual ? (
        <ul className="flex flex-col gap-(--mbm-space-3)" aria-label="Matching businesses">
          {places.map((place) => (
            <li key={place.placeId}>
              <PlaceRow
                name={place.name}
                address={place.formattedAddress}
                selected={false}
                onSelect={() =>
                  updateDraft({
                    businessName: place.name,
                    googlePlaceId: place.placeId,
                    address: place.formattedAddress ?? '',
                  })
                }
              />
            </li>
          ))}
        </ul>
      ) : null}

      {alreadyPicked ? (
        <PlaceRow name={draft.businessName} address={draft.address || null} selected onSelect={() => undefined} />
      ) : null}

      {showNoMatch ? (
        <div className="flex flex-col gap-(--mbm-space-2) rounded-(--mbm-component-radius-card) bg-surface-muted p-(--mbm-space-5)">
          <p className="text-heading-h4 text-text-primary">No match on Google Maps yet</p>
          <p className="text-body-small text-text-secondary">
            New businesses often aren’t listed. Add your address and we’ll set up your Google Business Profile for you.
          </p>
          <Button variant="secondary" fullWidth className="mt-(--mbm-space-2)" onClick={() => setManual(true)}>
            Add address manually
          </Button>
        </div>
      ) : null}

      {manual ? (
        // TODO(api): the profile endpoint has no address field yet, so a manual address is kept for display and not saved.
        <Input
          label="Street address & city"
          autoComplete="street-address"
          value={draft.address}
          onChange={(e) => updateDraft({ address: e.target.value, googlePlaceId: undefined })}
        />
      ) : !showNoMatch ? (
        <button type="button" onClick={() => setManual(true)} className={cx(textLinkClass, 'self-start')}>
          Can’t find it? Add your address manually
        </button>
      ) : null}

      <Button fullWidth disabled={!canContinue} onClick={() => navigate({ to: '/subdomain/app/onboarding/website' })}>
        Continue
      </Button>
    </>
  );
}

function PlaceRow({
  name,
  address,
  selected,
  onSelect,
}: {
  name: string;
  address: string | null;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cx(
        'flex w-full items-center gap-(--mbm-space-3) rounded-(--mbm-radius-md) border p-(--mbm-space-3) text-left',
        'focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)',
        selected
          ? 'border-2 border-brand-primary bg-tag-blue-bg'
          : 'border-border-input bg-surface-card hover:bg-surface-muted',
      )}
    >
      <span className="flex size-(--mbm-size-icon-tile-large) shrink-0 items-center justify-center rounded-(--mbm-component-radius-icon-tile) bg-surface-tint text-text-primary">
        <Icon icon={MapPin} size="md" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-label-large text-text-primary">{name}</span>
        {address ? <span className="truncate text-body-small text-text-secondary">{address}</span> : null}
      </span>
      {selected ? (
        <span className="text-brand-primary">
          <Icon icon={CheckCircle} size="lg" />
        </span>
      ) : null}
    </button>
  );
}
